import { useEffect, useMemo, useState } from 'react';
import { todoService } from '../services/todoService';
import { useAuth } from '../context/AuthContext';

export default function useTodos() {
  const { user } = useAuth();
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all'); // all|P1|P2|P3
  const [tagFilter, setTagFilter] = useState('');
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    if (!user) {
      // No user: clear list and ensure UI isn't stuck in loading state
      setTodos([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = todoService.subscribeUserTodos(user.uid, (list) => {
      const sorted = [...list].sort((a, b) => {
        // open < done < archived
        const ordStatus = (s) => (s === 'open' ? 0 : s === 'done' ? 1 : 2);
        const sa = ordStatus(a.status);
        const sb = ordStatus(b.status);
        if (sa !== sb) return sa - sb;
        const oa = a.order ?? 0;
        const ob = b.order ?? 0;
        return oa - ob;
      });
      setTodos(sorted);
      setLoading(false);
    });
    return () => unsub && unsub();
  }, [user]);

  const addTodo = async ({ title, priority = 'P2', estimatePomos = 0, tags = [], dueAt = null }) => {
    if (!user) return;
    await todoService.addTodo(user.uid, { title, priority, estimatePomos, tags, dueAt });
  };

  const toggleDone = async (todo) => {
    if (!user) return;
    const newStatus = todo.status === 'done' ? 'open' : 'done';
    await todoService.updateTodo(user.uid, todo.id, { status: newStatus, completedAt: newStatus === 'done' ? new Date() : null });
  };

  const updateTitle = async (todo, title) => {
    if (!user) return;
    await todoService.updateTodo(user.uid, todo.id, { title });
  };
  const updatePriority = async (todo, priority) => {
    if (!user) return;
    await todoService.updateTodo(user.uid, todo.id, { priority });
  };
  const updateEstimate = async (todo, estimatePomos) => {
    if (!user) return;
    await todoService.updateTodo(user.uid, todo.id, { estimatePomos });
  };
  const updateDueAt = async (todo, dueAt) => {
    if (!user) return;
    await todoService.updateTodo(user.uid, todo.id, { dueAt: dueAt ? new Date(dueAt) : null });
  };
  const updateTags = async (todo, tags) => {
    if (!user) return;
    await todoService.updateTodo(user.uid, todo.id, { tags });
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());
  const selectAllOpen = () => setSelected(new Set(todos.filter(t => t.status === 'open').map(t => t.id)));

  const bulkComplete = async () => {
    if (!user || selected.size === 0) return;
    const ids = Array.from(selected);
    await Promise.all(ids.map(async (id) => {
      const t = todos.find(x => x.id === id);
      if (t) await todoService.updateTodo(user.uid, id, { status: 'done', completedAt: new Date() });
    }));
    clearSelection();
  };
  const bulkDelete = async () => {
    if (!user || selected.size === 0) return;
    const ids = Array.from(selected);
    // Soft-delete: set status archived
    await Promise.all(ids.map(async (id) => {
      const t = todos.find(x => x.id === id);
      if (t) await todoService.updateTodo(user.uid, id, { status: 'archived' });
    }));
    clearSelection();
  };

  const archiveTodo = async (todo) => {
    if (!user) return;
    await todoService.updateTodo(user.uid, todo.id, { status: 'archived' });
  };

  const onReorder = async (orderedIds) => {
    if (!user) return;
    await todoService.reorder(user.uid, orderedIds);
  };

  // Checklist helpers
  const addChecklistItem = async (todo, text) => {
    if (!user || !text.trim()) return;
    const item = { id: Math.random().toString(36).slice(2), text: text.trim(), done: false };
    const checklist = Array.isArray(todo.checklist) ? [...todo.checklist, item] : [item];
    await todoService.updateTodo(user.uid, todo.id, { checklist });
  };
  const toggleChecklistItem = async (todo, itemId) => {
    if (!user) return;
    const checklist = (todo.checklist || []).map((it) => it.id === itemId ? { ...it, done: !it.done } : it);
    await todoService.updateTodo(user.uid, todo.id, { checklist });
  };
  const removeChecklistItem = async (todo, itemId) => {
    if (!user) return;
    const checklist = (todo.checklist || []).filter((it) => it.id !== itemId);
    await todoService.updateTodo(user.uid, todo.id, { checklist });
  };
  const editChecklistItem = async (todo, itemId, text) => {
    if (!user) return;
    const checklist = (todo.checklist || []).map((it) => it.id === itemId ? { ...it, text } : it);
    await todoService.updateTodo(user.uid, todo.id, { checklist });
  };

  const filtered = useMemo(() => {
    return todos.filter((t) => {
      const matchesText = !search || t.title.toLowerCase().includes(search.toLowerCase());
      const matchesPriority = priorityFilter === 'all' || t.priority === priorityFilter;
      const matchesTag = !tagFilter || (Array.isArray(t.tags) && t.tags.some(tag => tag.toLowerCase().includes(tagFilter.toLowerCase())));
      return matchesText && matchesPriority && matchesTag;
    });
  }, [todos, search, priorityFilter, tagFilter]);

  return {
    todos: filtered,
    loading,
    addTodo,
    toggleDone,
    updateTitle,
    updatePriority,
    updateEstimate,
    updateDueAt,
    updateTags,
    search,
    setSearch,
    priorityFilter,
    setPriorityFilter,
    tagFilter,
    setTagFilter,
    selected,
    toggleSelect,
    clearSelection,
    selectAllOpen,
    bulkComplete,
    bulkDelete,
    archiveTodo,
    onReorder,
    addChecklistItem,
    toggleChecklistItem,
    removeChecklistItem,
    editChecklistItem,
  };
}
